import useAuthStore from '../stores/auth-store';

// API base URLs - use proxy paths that are configured in setupProxy.js
const API_BASE_URLS = {
  manager: '',
  data: '/api/data',
  knowledge: '/api/knowledge'
};

// Default API request timeout (120 seconds — Snowflake queries via Lambda take 30-45s)
const DEFAULT_TIMEOUT_MS = 120000;

function authHeaders(extra = {}) {
  const token = localStorage.getItem('authToken');
  return {
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...extra,
  };
}

async function buildErrorFromResponse(response) {
  let detail = `HTTP ${response.status}`;
  try {
    const body = await response.json();
    if (body && (body.detail || body.message || body.error)) {
      detail = body.detail || body.message || body.error;
      if (Array.isArray(detail)) detail = detail.map(d => d.msg || JSON.stringify(d)).join('; ');
      else if (typeof detail !== 'string') detail = JSON.stringify(detail);
    }
  } catch {
    /* response had no JSON body */
  }
  if (response.status === 401) {
    useAuthStore.getState().clearAuth();
  }

  const err = new Error(detail);
  err.status = response.status;
  err.detail = detail;
  return err;
}

/**
 * Parse SSE (Server-Sent Events) stream
 * @param {ReadableStream} stream - The response body stream
 * @param {Function} onMessage - Callback for each message
 * @param {Function} onError - Callback for errors
 * @param {Function} onComplete - Callback when stream completes
 */
export async function parseSSEStream(stream, onMessage, onError, onComplete) {
  const reader = stream.getReader();
  const decoder = new TextDecoder();
  let buffer = '';
  let completeCalled = false;

  try {
    while (true) {
      const { done, value } = await reader.read();

      if (done) {
        if (onComplete && !completeCalled) {
          completeCalled = true;
          onComplete();
        }
        break;
      }

      buffer += decoder.decode(value, { stream: true });
      const lines = buffer.split('\n');
      buffer = lines.pop() || '';

      for (const line of lines) {
        if (line.startsWith('data: ')) {
          const data = line.slice(6);

          if (data === '[DONE]') {
            if (onComplete && !completeCalled) {
              completeCalled = true;
              onComplete();
            }
            return;
          }

          try {
            const parsed = JSON.parse(data);
            if (onMessage) onMessage(parsed);
          } catch (e) {
            console.error('Error parsing SSE data:', e);
            if (onError) onError(e);
          }
        }
      }
    }
  } catch (error) {
    console.error('Stream reading error:', error);
    if (onError) onError(error);
  }
}

/**
 * Make a POST request that returns SSE stream
 */
export async function streamRequest(service, endpoint, data, onMessage, onError, onComplete) {
  const baseUrl = API_BASE_URLS[service];
  const url = `${baseUrl}${endpoint}`;

  try {
    const response = await fetch(url, {
      method: 'POST',
      headers: authHeaders(),
      body: JSON.stringify(data)
    });

    if (!response.ok) {
      throw await buildErrorFromResponse(response);
    }

    await parseSSEStream(response.body, onMessage, onError, onComplete);
  } catch (error) {
    console.error('Stream request error:', error);
    if (onError) onError(error);
  }
}

/**
 * Make a regular POST request
 */
export async function postRequest(service, endpoint, data, options = {}) {
  const baseUrl = API_BASE_URLS[service];
  const url = `${baseUrl}${endpoint}`;

  const response = await fetch(url, {
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify(data),
    signal: options.signal
      ? AbortSignal.any([options.signal, AbortSignal.timeout(DEFAULT_TIMEOUT_MS)])
      : AbortSignal.timeout(DEFAULT_TIMEOUT_MS),
  });

  if (!response.ok) {
    throw await buildErrorFromResponse(response);
  }

  return await response.json();
}

/**
 * Make a GET request
 */
export async function getRequest(service, endpoint, params = {}, options = {}) {
  const baseUrl = API_BASE_URLS[service];
  const cleanParams = Object.fromEntries(
    Object.entries(params).filter(([_, v]) => v != null)
  );
  const queryString = new URLSearchParams(cleanParams).toString();
  const url = `${baseUrl}${endpoint}${queryString ? '?' + queryString : ''}`;

  const timeoutMs = options.timeout || DEFAULT_TIMEOUT_MS;
  const response = await fetch(url, {
    method: 'GET',
    headers: authHeaders(),
    signal: options.signal
      ? AbortSignal.any([options.signal, AbortSignal.timeout(timeoutMs)])
      : AbortSignal.timeout(timeoutMs),
  });

  if (!response.ok) {
    throw await buildErrorFromResponse(response);
  }

  return await response.json();
}

/**
 * Make a PUT request
 */
export async function putRequest(service, endpoint, data) {
  const baseUrl = API_BASE_URLS[service];
  const url = `${baseUrl}${endpoint}`;

  const response = await fetch(url, {
    method: 'PUT',
    headers: authHeaders(),
    body: JSON.stringify(data),
    signal: AbortSignal.timeout(DEFAULT_TIMEOUT_MS),
  });

  if (!response.ok) {
    throw await buildErrorFromResponse(response);
  }

  return await response.json();
}

export async function patchRequest(service, endpoint, data) {
  const baseUrl = API_BASE_URLS[service];
  const url = `${baseUrl}${endpoint}`;

  const response = await fetch(url, {
    method: 'PATCH',
    headers: authHeaders(),
    body: JSON.stringify(data),
    signal: AbortSignal.timeout(DEFAULT_TIMEOUT_MS),
  });

  if (!response.ok) {
    throw await buildErrorFromResponse(response);
  }

  return await response.json();
}

export async function deleteRequest(service, endpoint, data) {
  const baseUrl = API_BASE_URLS[service];
  const url = `${baseUrl}${endpoint}`;

  const opts = { method: 'DELETE', headers: authHeaders(), signal: AbortSignal.timeout(DEFAULT_TIMEOUT_MS) };
  if (data !== undefined) opts.body = JSON.stringify(data);

  const response = await fetch(url, opts);

  if (!response.ok) {
    throw await buildErrorFromResponse(response);
  }

  if (response.status === 204) return null;

  const text = await response.text();
  return text ? JSON.parse(text) : null;
}

export default {
  streamRequest,
  postRequest,
  getRequest,
  putRequest,
  patchRequest,
  deleteRequest,
  parseSSEStream
};
